import { useEffect, useState } from "react";
import config from '../../config';
import './DueCus.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { ChevronUp, ChevronDown, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import React from 'react';

const DatedCheques = () => {
  const expandedColumns = ['Invoice Number', 'Customer Name', 'Customer Office', 'Cheque Details', 'Dated Cheques', 'Paid Amount', 'Days Left', 'Action'];

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchDueCustomer();
  }, []);

  const fetchCustomerName = async (cusId) => {
    try {
      const response = await fetch(`${config.BASE_URL}/customer/${cusId}`);
      if (response.ok) {
        const customer = await response.json();
        return customer.cusName;
      }
      return 'Unknown Customer';
    } catch {
      return 'Unknown Customer';
    }
  };

  const fetchInvoiceNumber = async (invoiceId) => {
    try {
      const response = await fetch(`${config.BASE_URL}/invoice/${invoiceId}`);
      if (response.ok) {
        const invoice = await response.json();
        return invoice.invoiceNo;
      }
      return 'Unknown Invoice';
    } catch {
      return 'Unknown Invoice';
    }
  };

  const calculateDaysLeft = (datedCheque) => {
    const today = new Date();
    const chequeDate = new Date(datedCheque);
    today.setHours(0, 0, 0, 0);
    chequeDate.setHours(0, 0, 0, 0);

    const timeDifference = chequeDate.getTime() - today.getTime();
    const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

    if (daysDifference === 0) {
      return { text: "Due Today", color: "orange" };
    } else if (daysDifference < 0) {
      return { text: "Clear", color: "red" };
    } else {
      return { text: `${daysDifference} Days Left`, color: "green" };
    }
  };

  const fetchDueCustomer = async () => {
    try {
      const response = await fetch(`${config.BASE_URL}/duecustomers`);
      if (!response.ok) {
        setError('Failed to fetch Due Customers');
        return;
      }
      const dueCustomers = await response.json();
      const chequeDueCustomers = dueCustomers.filter(dueCustomer => dueCustomer.payType === 'cheque');

      const customerNameCache = {};
      const invoiceNumberCache = {};

      const groupedData = chequeDueCustomers.reduce((acc, dueCustomer) => {
        const key = `${dueCustomer.invoiceId}-${dueCustomer.cusId}`;
        if (!acc[key]) {
          acc[key] = {
            invoiceId: dueCustomer.invoiceId,
            cusId: dueCustomer.cusId,
            transactions: [],
            totalPaidAmount: 0
          };
        }
        acc[key].transactions.push(dueCustomer);
        acc[key].totalPaidAmount += dueCustomer.paidAmount;
        return acc;
      }, {});

      const formattedData = await Promise.all(
        Object.values(groupedData).map(async (group) => {
          let customerName = customerNameCache[group.cusId];
          if (!customerName) {
            customerName = await fetchCustomerName(group.cusId);
            customerNameCache[group.cusId] = customerName;
          }

          let invoiceNo = invoiceNumberCache[group.invoiceId];
          if (!invoiceNo) {
            invoiceNo = await fetchInvoiceNumber(group.invoiceId);
            invoiceNumberCache[group.invoiceId] = invoiceNo;
          }

          return {
            invoiceId: group.invoiceId,
            invoiceNo,
            customerName,
            customerOffice: group.transactions[0].customer?.cusOffice || '-',
            cusId: group.cusId,
            transactions: group.transactions,
            totalPaidAmount: group.totalPaidAmount
          };
        })
      );

      setData(formattedData.filter(Boolean));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRowExpansion = (key) => {
    setExpandedRows(prev =>
      prev.includes(key)
        ? prev.filter(item => item !== key)
        : [...prev, key]
    );
  };

  const handleViewDetails = (cusId) => {
    if (!cusId || cusId === "-") {
      alert("Customer ID not available");
      return;
    }
    navigate(`/cus-due/${cusId}`);
  };

  return (
    <div>
      <div className="scrolling-container">
        <div className="new-sales-container">
          <h4>Dated Cheques</h4>

          {isLoading ? (
            <p>Loading...</p>
          ) : error ? (
            <p>No Data: {error}</p>
          ) : null}

          <table className="due-cus-table">
            <thead>
              <tr>
                {expandedColumns.map((column, index) => (
                  <th key={index}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row) => {
                const rowKey = `${row.invoiceId}-${row.cusId}`;
                const isExpanded = expandedRows.includes(rowKey);
                const hasMultipleTransactions = row.transactions.length > 1;
                const firstTransaction = row.transactions[0];

                return (
                  <React.Fragment key={rowKey}>
                    <tr>
                      <td>{row.invoiceNo}</td>
                      <td>{row.customerName}</td>
                      <td>{row.customerOffice}</td>
                      <td>{hasMultipleTransactions ? '-' : firstTransaction.chequeDetail}</td>
                      <td>{hasMultipleTransactions ? '-' : new Date(firstTransaction.datedCheque).toLocaleString()}</td>
                      <td>{hasMultipleTransactions ? row.totalPaidAmount : firstTransaction.paidAmount}</td>
                      <td>
                        {hasMultipleTransactions ? '-' : (
                          <span style={{ color: calculateDaysLeft(firstTransaction.datedCheque).color }}>
                            {calculateDaysLeft(firstTransaction.datedCheque).text}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex justify-content-between align-items-center">

                        <button
                            className="btn btn-warning ms-2"
                            onClick={() => handleViewDetails(row.cusId)}
                          >
                            <Eye size={16} />
                          </button>
                          
                          {hasMultipleTransactions && (
                            <button
                              className="btn btn-primary"
                              onClick={() => toggleRowExpansion(rowKey)}
                            >
                              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                          )}
                          
                        </div>
                      </td>
                    </tr>
                    {isExpanded && hasMultipleTransactions && (
                      <tr className="expanded-row">
                        <td colSpan={expandedColumns.length}>
                          <table className="expanded-table">
                            <thead>
                              <tr>
                                {expandedColumns.slice(0, -1).map((column, index) => ( // Exclude 'Action' from expanded table
                                  <th key={index}>{column}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {row.transactions.map((transaction, tIndex) => {
                                const daysLeftInfo = calculateDaysLeft(transaction.datedCheque);
                                return (
                                  <tr key={`${rowKey}-${tIndex}`}>
                                    <td>{row.invoiceNo}</td>
                                    <td>{row.customerName}</td>
                                    <td>{row.customerOffice}</td>
                                    <td>{transaction.chequeDetail}</td>
                                    <td>{new Date(transaction.datedCheque).toLocaleString()}</td>
                                    <td>{transaction.paidAmount}</td>
                                    <td style={{ color: daysLeftInfo.color }}>{daysLeftInfo.text}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DatedCheques;

// import { useEffect, useState } from "react";
// import config from '../../config';
// import './DueCus.css';
// import 'bootstrap/dist/css/bootstrap.min.css'; // Add Bootstrap CSS

// const DatedCheques = () => {
//   const expandedColumns = ['Invoice Number', 'Customer Name', 'Customer Office', 'Cheque Details', 'Dated Cheques', 'Paid Amount', 'Days Left', 'Action'];
  
//   const [data, setData] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [expandedRows, setExpandedRows] = useState({}); // Track expanded rows

//   useEffect(() => {
//     fetchDueCustomer();
//   }, []);

//   const fetchCustomerName = async (cusId) => {
//     try {
//       const response = await fetch(`${config.BASE_URL}/customer/${cusId}`);
//       if (response.ok) {
//         const customer = await response.json();
//         return customer.cusName;
//       }
//       return 'Unknown Customer';
//     } catch {
//       return 'Unknown Customer';
//     }
//   };

//   const fetchInvoiceNumber = async (invoiceId) => {
//     try {
//       const response = await fetch(`${config.BASE_URL}/invoice/${invoiceId}`);
//       if (response.ok) {
//         const invoice = await response.json();
//         return invoice.invoiceNo;
//       }
//       return 'Unknown Invoice';
//     } catch {
//       return 'Unknown Invoice';
//     }
//   };

//   const calculateDaysLeft = (datedCheque) => {
//     const today = new Date();
//     const chequeDate = new Date(datedCheque);
//     today.setHours(0, 0, 0, 0);
//     chequeDate.setHours(0, 0, 0, 0);

//     const timeDifference = chequeDate.getTime() - today.getTime();
//     const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

//     if (daysDifference === 0) {
//       return { text: "Due Today", color: "orange" };
//     } else if (daysDifference < 0) {
//       return { text: "Clear", color: "red" };
//     } else {
//       return { text: `${daysDifference} Days Left`, color: "green" };
//     }
//   };

//   const fetchDueCustomer = async () => {
//     try {
//       const response = await fetch(`${config.BASE_URL}/duecustomers`);
//       if (!response.ok) {
//         setError('Failed to fetch Due Customers');
//         return;
//       }
//       const dueCustomers = await response.json();
//       const chequeDueCustomers = dueCustomers.filter(dueCustomer => dueCustomer.payType === 'cheque');

//       const customerNameCache = {};
//       const invoiceNumberCache = {};

//       // Group data by invoiceNo, customerName, and customerOffice
//       const groupedData = chequeDueCustomers.reduce((acc, dueCustomer) => {
//         const key = `${dueCustomer.invoiceId}-${dueCustomer.cusId}`;
//         if (!acc[key]) {
//           acc[key] = {
//             invoiceId: dueCustomer.invoiceId,
//             cusId: dueCustomer.cusId,
//             transactions: [],
//             totalPaidAmount: 0
//           };
//         }
//         acc[key].transactions.push(dueCustomer);
//         acc[key].totalPaidAmount += dueCustomer.paidAmount;
//         return acc;
//       }, {});

//       const formattedData = await Promise.all(
//         Object.values(groupedData).map(async (group) => {
//           let customerName = customerNameCache[group.cusId];
//           if (!customerName) {
//             customerName = await fetchCustomerName(group.cusId);
//             customerNameCache[group.cusId] = customerName;
//           }

//           let invoiceNo = invoiceNumberCache[group.invoiceId];
//           if (!invoiceNo) {
//             invoiceNo = await fetchInvoiceNumber(group.invoiceId);
//             invoiceNumberCache[group.invoiceId] = invoiceNo;
//           }

//           return {
//             invoiceId: group.invoiceId,
//             invoiceNo,
//             customerName,
//             customerOffice: group.transactions[0].customer?.cusOffice || '-',
//             cusId: group.cusId,
//             transactions: group.transactions,
//             totalPaidAmount: group.totalPaidAmount
//           };
//         })
//       );

//       setData(formattedData.filter(Boolean));
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const toggleRow = (index) => {
//     setExpandedRows(prev => ({
//       ...prev,
//       [index]: !prev[index]
//     }));
//   };

//   return (
//     <div>
//       <div className="scrolling-container">
//         <div className="new-sales-container">
//           <h4>Dated Cheques</h4>

//           {isLoading ? (
//             <p>Loading...</p>
//           ) : error ? (
//             <p>No Data: {error}</p>
//           ) : null}

//           <table className="due-cus-table">
//             <thead>
//               <tr>
//                 {expandedColumns.map((column, index) => (
//                   <th key={index}>{column}</th>
//                 ))}
//               </tr>
//             </thead>
//             <tbody>
//               {data.map((item, index) => {
//                 const isExpanded = expandedRows[index];
//                 const hasMultipleTransactions = item.transactions.length > 1;

//                 return (
//                   <>
//                     <tr key={index}>
//                       <td>{item.invoiceNo}</td>
//                       <td>{item.customerName}</td>
//                       <td>{item.customerOffice}</td>
//                       <td>{hasMultipleTransactions ? '-' : item.transactions[0].chequeDetail}</td>
//                       <td>{hasMultipleTransactions ? '-' : new Date(item.transactions[0].datedCheque).toLocaleString()}</td>
//                       <td>{item.totalPaidAmount}</td>
//                       <td>
//                         {hasMultipleTransactions ? '-' : (
//                           <span style={{ color: calculateDaysLeft(item.transactions[0].datedCheque).color }}>
//                             {calculateDaysLeft(item.transactions[0].datedCheque).text}
//                           </span>
//                         )}
//                       </td>
//                       <td>
//                         {hasMultipleTransactions && (
//                           <div className="dropdown">
//                             <button
//                               className="btn btn-primary dropdown-toggle"
//                               type="button"
//                               onClick={() => toggleRow(index)}
//                             >
//                               <span className="visually-hidden">Toggle Dropdown</span>
//                             </button>
//                           </div>
//                         )}
//                       </td>
//                     </tr>
//                     {isExpanded && hasMultipleTransactions && item.transactions.map((transaction, tIndex) => {
//                       const daysLeftInfo = calculateDaysLeft(transaction.datedCheque);
//                       return (
//                         <tr key={`${index}-${tIndex}`} className="expanded-row">
//                           <td>{item.invoiceNo}</td>
//                           <td>{item.customerName}</td>
//                           <td>{item.customerOffice}</td>
//                           <td>{transaction.chequeDetail}</td>
//                           <td>{new Date(transaction.datedCheque).toLocaleString()}</td>
//                           <td>{transaction.paidAmount}</td>
//                           <td style={{ color: daysLeftInfo.color }}>{daysLeftInfo.text}</td>
//                           <td></td>
//                         </tr>
//                       );
//                     })}
//                   </>
//                 );
//               })}
//             </tbody>
//           </table>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default DatedCheques;






//v1 without expand

// import { useEffect, useState } from "react";
// import config from '../../config';
// import './DueCus.css';

// const DatedCheques = () => {
//   const expandedColumns = ['Invoice Number', 'Customer Name','Customer Office' ,'Cheque Details', 'Dated Cheques', 'Paid Amount', 'Days Left']; // Added 'Days Left'

//   const [data, setData] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     fetchDueCustomer();
//   }, []);

//   const fetchCustomerName = async (cusId) => {
//     try {
//       const response = await fetch(`${config.BASE_URL}/customer/${cusId}`);
//       if (response.ok) {
//         const customer = await response.json();
//         return customer.cusName;
//       }
//       return 'Unknown Customer';
//     } catch {
//       return 'Unknown Customer';
//     }
//   };

//   const fetchInvoiceNumber = async (invoiceId) => {
//     try {
//       const response = await fetch(`${config.BASE_URL}/invoice/${invoiceId}`);
//       if (response.ok) {
//         const invoice = await response.json();
//         return invoice.invoiceNo;
//       }
//       return 'Unknown Invoice';
//     } catch {
//       return 'Unknown Invoice';
//     }
//   };

//   // Function to calculate days left for the cheque
//   const calculateDaysLeft = (datedCheque) => {
//     const today = new Date();
//     const chequeDate = new Date(datedCheque);

//     // Set both dates to the start of the day (midnight) for accurate comparison
//     today.setHours(0, 0, 0, 0);
//     chequeDate.setHours(0, 0, 0, 0);

//     const timeDifference = chequeDate.getTime() - today.getTime();
//     const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

//     if (daysDifference === 0) {
//       return { text: "Due Today", color: "orange" };
//     } else if (daysDifference < 0) {
//       return { text: "Clear", color: "red" };
//     } else {
//       return { text: `${daysDifference} Days Left`, color: "green" };
//     }
//   };

//   const fetchDueCustomer = async () => {
//     try {
//       const response = await fetch(`${config.BASE_URL}/duecustomers`); // Fetch from dueCustomer table
//       if (!response.ok) {
//         setError('Failed to fetch Due Customers');
//         return;
//       }
//       const dueCustomers = await response.json();

//       // Filter only rows where payType is 'cheque'
//       const chequeDueCustomers = dueCustomers.filter(dueCustomer => dueCustomer.payType === 'cheque');

//       const customerNameCache = {}; // Cache to avoid redundant API calls for the same cusId
//       const invoiceNumberCache = {}; // Cache to avoid redundant API calls for the same invoiceId

//       const formattedData = await Promise.all(
//         chequeDueCustomers.map(async (dueCustomer) => {
//           let customerName = customerNameCache[dueCustomer.cusId];
//           if (!customerName) {
//             customerName = await fetchCustomerName(dueCustomer.cusId);
//             customerNameCache[dueCustomer.cusId] = customerName; // Store in cache
//           }

//           let invoiceNo = invoiceNumberCache[dueCustomer.invoiceId];
//           if (!invoiceNo) {
//             invoiceNo = await fetchInvoiceNumber(dueCustomer.invoiceId);
//             invoiceNumberCache[dueCustomer.invoiceId] = invoiceNo; // Store in cache
//           }

//           return {
//             invoiceId: dueCustomer.invoiceId,
//             invoiceNo, // Fetch invoice number
//             customerName, // Dynamically fetched customer name
//             customerOffice:dueCustomer.customer?.cusOffice || '-',
//             cusId: dueCustomer.cusId,
//             chequeDetail: dueCustomer.chequeDetail,
//             datedCheque: dueCustomer.datedCheque, // Fetch datedCheque from dueCustomer table
//             paidAmount: dueCustomer.paidAmount, // Fetch paidAmount from dueCustomer table
//           };
//         })
//       );

//       setData(formattedData.filter(Boolean));
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   return (
//     <div>
//       <div className="scrolling-container">
//         <div className="new-sales-container">
//           <h4>Dated Cheques</h4>

//           {isLoading ? (
//             <p>Loading...</p>
//           ) : error ? (
//             <p>No Data: {error}</p>
//           ) : null}

//           <table className="due-cus-table">
//             <thead>
//               <tr>
//                 {expandedColumns.map((column, index) => (
//                   <th key={index}>{column}</th>
//                 ))}
//               </tr>
//             </thead>
//             <tbody>
//               {data.map((item, index) => {
//                 const daysLeftInfo = calculateDaysLeft(item.datedCheque);
//                 return (
//                   <tr key={index}>
//                     <td>{item.invoiceNo}</td> {/* Display invoice number */}
//                     <td>{item.customerName}</td> {/* Display customer name */}
//                     <td>{item.customerOffice}</td> {/* Display customer name */}

//                     <td>{item.chequeDetail}</td>  
                    
//                     <td>{new Date(item.datedCheque).toLocaleString()}</td> {/* Display datedCheque */}
                   
//                     <td>{item.paidAmount}</td> {/* Display paidAmount */}
//                     <td style={{ color: daysLeftInfo.color }}>{daysLeftInfo.text}</td> {/* Display days left with color */}
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default DatedCheques;